import { Component } from '@ribajs/core'
import { hasChildNodesTrim } from '@ribajs/utils'

import { SbTabsComponent } from './sb-tabs.component'

interface SbLanguageSwitcherScope {
  languageSectionVisible: boolean
  tabVisibilityConditions: Record<string, boolean>
  isSubmitting: boolean
  availableLanguagesPerMarket: Record<string, any[]>
}

interface ShopLocale {
  iso_code: string
  primary: boolean
  root_url: string
  endonym_name: string
  name: string
}

interface LanguageObject {
  shop_locale: ShopLocale
}

export class SbLanguageSwitcherComponent extends Component {
  public static tagName = 'sb-language-switcher'
  protected autobind = true

  static get observedAttributes(): string[] {
    return ['available-languages-per-market']
  }

  protected formEl: HTMLFormElement | null = null
  protected countrySectionEl: HTMLElement | null = null
  protected countryInputEl: NodeListOf<HTMLInputElement> | null = null
  protected countryLabelEl: NodeListOf<HTMLLabelElement> | null = null
  protected languageSectionEl: HTMLElement | null = null
  protected languageInputEl: NodeListOf<HTMLInputElement> | null = null
  protected languageLabelEl: NodeListOf<HTMLLabelElement> | null = null
  protected redirectMessageEl: HTMLElement | null = null
  protected selectedCountryName: string = ''
  protected selectedLanguageName: string = ''
  protected selectedLanguageSpan: HTMLElement | null = null

  private isInitializing: boolean = false

  // Use the updated interface name
  public scope: SbLanguageSwitcherScope = {
    languageSectionVisible: false,
    tabVisibilityConditions: { 'language-section': false },
    isSubmitting: false,
    availableLanguagesPerMarket: {},
  }

  constructor() {
    super()

    this.onCountryChange = this.onCountryChange.bind(this)
    this.onLanguageChange = this.onLanguageChange.bind(this)
    this.onFormSubmit = this.onFormSubmit.bind(this)
  }

  protected radioKeydownHandlers = new WeakMap<
    HTMLInputElement,
    (e: KeyboardEvent) => void
  >()

  protected radioChangeHandlers = new WeakMap<HTMLInputElement, () => void>()
  protected mutationObserver: MutationObserver | null = null
  private timeoutId: number | null = null

  protected connectedCallback() {
    super.connectedCallback()
    this.isInitializing = true
    this.init(SbLanguageSwitcherComponent.observedAttributes)

    // Handle both success and failure cases
    this.waitForLocalizationForm()
      .then(() => {
        this.findElements()
        this.addEventListeners()
        this.checkInitialState()
        this.isInitializing = false
      })
      .catch(error => {
        console.error('Failed to initialize language switcher:', error)
        this.isInitializing = false
      })
  }

  private cleanup() {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect()
      this.mutationObserver = null
    }
    if (this.timeoutId) {
      clearTimeout(this.timeoutId)
      this.timeoutId = null
    }
  }

  private retryCount = 0
  private maxRetries = 3
  private retryDelay = 1000 // 1 second

  protected waitForLocalizationForm(): Promise<HTMLFormElement> {
    return new Promise((resolve, reject) => {
      let isResolved = false // Prevent multiple resolutions

      const resolveOnce = (form: HTMLFormElement) => {
        if (isResolved) return
        isResolved = true
        this.cleanup()
        resolve(form)
      }

      const rejectOnce = (error: Error) => {
        if (isResolved) return
        isResolved = true
        this.cleanup()
        reject(error)
      }

      const existingForm = this.querySelector(
        '#localization_form',
      ) as HTMLFormElement
      if (existingForm) {
        resolveOnce(existingForm)
        return
      }

      this.cleanup()

      this.mutationObserver = new MutationObserver(_mutations => {
        const form = document.querySelector(
          '#localization_form',
        ) as HTMLFormElement
        if (form) {
          resolveOnce(form)
        }
      })

      // Start observing changes to the component's children
      this.mutationObserver.observe(this, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['id'],
      })

      // Timeout after 2 seconds to avoid hanging indefinitely
      this.timeoutId = window.setTimeout(() => {
        const form = document.querySelector(
          '#localization_form',
        ) as HTMLFormElement
        if (form) {
          resolveOnce(form)
        } else {
          console.warn('Localization form not found after timeout')
          rejectOnce(
            new Error('Localization form not found within timeout period'),
          )
        }
      }, 8000)
    })
  }

  private async handleInitializationFailure() {
    // Strategy 1: Retry with exponential backoff
    if (this.retryCount < this.maxRetries) {
      this.retryCount++
      const delay = this.retryDelay * Math.pow(2, this.retryCount - 1)
      console.log(
        `Retrying initialization in ${delay}ms (attempt ${this.retryCount}/${this.maxRetries})`,
      )

      setTimeout(() => {
        this.waitForLocalizationForm()
          .then(() => {
            console.log('Retry successful!')
            this.retryCount = 0 // Reset on success
            this.findElements()
            this.addEventListeners()
            this.checkInitialState()
          })
          .catch(() => this.handleInitializationFailure())
      }, delay)
      return
    }
  }

  protected disconnectedCallback() {
    super.disconnectedCallback()
    this.cleanup()
    this.removeEventListeners()
    if (this.mutationObserver) {
      this.mutationObserver.disconnect()
      this.mutationObserver = null
    }
  }

  protected findElements() {
    // Refresh element references to avoid stale NodeLists
    this.formEl =
      this.closest('form') || document.querySelector('#localization_form')
    this.countrySectionEl = this.querySelector('.country-section')
    this.countryInputEl = this.querySelectorAll<HTMLInputElement>(
      'input[name="country_code"]',
    )
    this.countryLabelEl = this.querySelectorAll<HTMLLabelElement>(
      'label[for*="country"]',
    ) as NodeListOf<HTMLLabelElement>
    this.languageSectionEl = this.querySelector('.language-section')
    this.languageInputEl = this.querySelectorAll<HTMLInputElement>(
      'input[name="language_code"]',
    )
    this.languageLabelEl = this.querySelectorAll<HTMLLabelElement>(
      'label[for*="language"]',
    ) as NodeListOf<HTMLLabelElement>
    this.selectedLanguageSpan = this.querySelector('.selectedLanguage')
    this.redirectMessageEl = this.querySelector('.redirect-message')
  }

  protected checkInitialState() {
    if (!this.countrySectionEl) {
      console.warn('Country section not found for initial check.')
      return
    }
    const selectedCountry =
      this.countrySectionEl.querySelector<HTMLInputElement>(
        'input[name="country_code"]:checked',
      )
    if (selectedCountry) {
      this.scope.languageSectionVisible = true

      // Find the tab component and update its visibility attribute
      const tabsEl = this.querySelector('sb-tabs') as SbTabsComponent
      if (tabsEl) {
        tabsEl.setAttribute('language-section-visible', 'true')
        if (tabsEl.showByName) {
          tabsEl.showByName('language-section')
        }
      }
    } else {
      this.scope.languageSectionVisible = false
    }
  }

  onCountryChange(event: Event) {
    if (this.isInitializing) {
      return
    }

    let input: HTMLInputElement | null = null
    let isUserClick = false

    // Check if this is a user-initiated event
    if (event.type === 'click' || event.type === 'change') {
      if (
        event.target instanceof HTMLInputElement &&
        event.target.name === 'country_code'
      ) {
        input = event.target
        isUserClick = true
      } else if (event.target instanceof HTMLLabelElement) {
        const forAttr = event.target.getAttribute('for')
        if (forAttr) {
          input =
            this.countrySectionEl?.querySelector<HTMLInputElement>(
              `#${forAttr}`,
            ) || null
          isUserClick = true
        }
      }
    }

    // Only proceed if this is a user-initiated event
    if (!isUserClick || !input) {
      return
    }

    this.setCountrySelection(input)
  }

  private updateLanguageOptions(availableLanguages: LanguageObject[]) {
    const languageContainer = this.querySelector('#language-options-container')
    if (!languageContainer) return

    // Clear previous language options
    languageContainer.innerHTML = ''

    // Add new language options based on the available languages
    availableLanguages.forEach((language, index) => {
      const languageOption = document.createElement('div')
      languageOption.className = 'country-language-selector__option-item'

      const tabindex = index === 0 ? '0' : '-1'

      languageOption.innerHTML = `
    <input
      type='radio'
      name='language_code'
      tabindex='${tabindex}'
      id='language-${language.shop_locale.iso_code}'
      value='${language.shop_locale.iso_code}'
      class='absolute -z-10 peer/language'
    >
    <label
      for='language-${language.shop_locale.iso_code}'
      class='country-language-selector__option-label transition-colors bg-grey-50 hover:bg-grey-100 active:bg-grey-300 peer-checked/language:bg-celtic-green peer-checked/language:hover:bg-celtic-green'
    >
      <span class='country-language-selector__option-text peer-checked/language:text-white'>
        ${language.shop_locale.endonym_name}
      </span>
    </label>
    <span
      class='country-language-selector__check-icon hidden peer-checked/language:inline'
    >
      <svg class="h-4 w-4" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
        <path d="M13.3337 4L6.00033 11.3333L2.66699 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </span>
    `

      languageContainer.appendChild(languageOption)
    })

    this.languageInputEl = this.querySelectorAll<HTMLInputElement>(
      'input[name="language_code"]',
    )
    this.languageLabelEl = this.querySelectorAll<HTMLLabelElement>(
      'label[for*="language"]',
    )

    if (this.languageInputEl && this.languageLabelEl) {
      this.languageInputEl.forEach(input => {
        input.removeEventListener('change', this.onLanguageChange)
        input.addEventListener('change', this.onLanguageChange)
      })

      this.languageLabelEl.forEach(label => {
        label.removeEventListener('click', this.onLanguageChange)
        label.addEventListener('click', this.onLanguageChange)
      })
    }
  }

  private setCountrySelection(input: HTMLInputElement) {
    const countryValue = input.value
    let labelName = ''

    const label = this.countrySectionEl?.querySelector(
      `label[for="country-${countryValue}"]`,
    )
    if (label) {
      const textSpan = label.querySelector(
        '.country-language-selector__option-text',
      )
      labelName =
        textSpan?.textContent?.trim() || label.textContent?.trim() || ''
    }

    this.selectedCountryName = labelName || countryValue

    // Get the available languages for this country
    let availableLanguagesArray: LanguageObject[] = []
    try {
      const availableLanguagesObj = input.dataset.availableLanguages
        ? JSON.parse(input.dataset.availableLanguages)
        : {}

      availableLanguagesArray = Object.values(availableLanguagesObj).map(
        (lang): LanguageObject => ({
          shop_locale: lang as ShopLocale,
        }),
      )
    } catch (error) {
      console.error('Error parsing available languages:', error)
      availableLanguagesArray = []
    }

    this.updateLanguageOptions(availableLanguagesArray)
    this.scope.languageSectionVisible = true
    this.scope.tabVisibilityConditions = { 'language-section': true }

    const tabsEl = this.querySelector('sb-tabs') as SbTabsComponent
    if (tabsEl) {
      tabsEl.setAttribute('language-section-visible', 'true')
      requestAnimationFrame(() => {
        if (tabsEl.showByName) {
          tabsEl.showByName('language-section')
        }
      })
    }
  }

  #resetState() {
    ;(this.querySelector('sb-tabs') as SbTabsComponent)?.show(0)
    this.querySelectorAll<HTMLInputElement>('input[type="radio"]').forEach(
      input => {
        input.checked = false
      },
    )

    const returnToInput = document.querySelector<HTMLInputElement>(
      '#localization_form input[name="return_to"]',
    )

    if (returnToInput) {
      // Make sure to redirect to the current url
      returnToInput.value =
        window.location.href.match(/\/\/[^\/]+(.*)/)?.[1] || '/'
    }
  }

  #onPopUpStateChange = (event: Event) => {
    const isVisible = (event as CustomEvent).detail?.visible
    if (isVisible) this.#resetState()
  }

  onLanguageChange(event: Event) {
    const target = event.target as HTMLInputElement
    if (target.name === 'language_code' && target.checked && this.formEl) {
      const label = this.querySelector(`label[for="language-${target.value}"]`)
      if (label) {
        const textSpan = label.querySelector(
          '.country-language-selector__option-text',
        )
        this.selectedLanguageName =
          textSpan?.textContent?.trim() || target.value
      } else {
        this.selectedLanguageName = target.value
      }

      this.scope.isSubmitting = true

      if (this.selectedLanguageSpan) {
        this.selectedLanguageSpan.textContent = `(${this.selectedCountryName} / ${this.selectedLanguageName})`
      }

      if (this.redirectMessageEl) {
        this.redirectMessageEl.classList.remove('hidden')
      }

      // Small delay to ensure the message is visible before submitting
      setTimeout(() => {
        this.formEl?.submit()
      }, 200)
    }
  }

  protected onFormSubmit = (event: Event) => {
    event.preventDefault()
  }

  protected addEventListeners() {
    this.findElements()

    if (this.formEl) {
      this.formEl.removeEventListener('submit', this.onFormSubmit)
      this.formEl.addEventListener('submit', this.onFormSubmit)
    }
    if (this.countryInputEl && this.countryLabelEl) {
      this.countryInputEl.forEach(input => {
        // Remove existing listeners first to prevent duplicates
        input.removeEventListener('change', this.onCountryChange)
        input.addEventListener('change', this.onCountryChange)
      })

      this.countryLabelEl.forEach(label => {
        // Remove existing listeners first to prevent duplicates
        label.removeEventListener('click', this.onCountryChange)
        label.addEventListener('click', this.onCountryChange)
      })
    }

    if (this.languageInputEl && this.languageLabelEl) {
      this.languageInputEl.forEach(input => {
        input.removeEventListener('change', this.onLanguageChange)
        input.addEventListener('change', this.onLanguageChange)
      })

      this.languageLabelEl.forEach(label => {
        label.removeEventListener('click', this.onLanguageChange)
        label.addEventListener('click', this.onLanguageChange)
      })
    }

    this.closest('sb-popup')?.addEventListener(
      'state-changed',
      this.#onPopUpStateChange,
    )
  }

  protected removeEventListeners() {
    if (this.formEl) {
      this.formEl.removeEventListener('submit', this.onFormSubmit)
    }
    // Existing cleanup
    if (this.countryInputEl) {
      this.countryInputEl.forEach(input => {
        input.removeEventListener('change', this.onCountryChange)
        input.removeEventListener('click', this.onCountryChange)
        input.removeEventListener('focus', this.onCountryChange)
        input.removeEventListener('keydown', this.onCountryChange)
      })
    }

    if (this.countryLabelEl) {
      this.countryLabelEl.forEach(label => {
        label.removeEventListener('click', this.onCountryChange)
      })
    }

    if (this.languageInputEl) {
      this.languageInputEl.forEach(input => {
        input.removeEventListener('change', this.onLanguageChange)
      })
    }

    if (this.languageLabelEl) {
      this.languageLabelEl.forEach(label => {
        label.removeEventListener('click', this.onLanguageChange)
      })
    }

    this.closest('sb-popup')?.removeEventListener(
      'state-changed',
      this.#onPopUpStateChange,
    )
  }

  async afterBind() {
    await super.afterBind()
  }

  protected requiredAttributes(): string[] {
    return []
  }

  protected template(): ReturnType<any> | null {
    // Don't render the component template - use the existing light DOM
    if (hasChildNodesTrim(this)) {
      return null
    } else {
      console.warn('Language switcher requires child elements (the form).')
      return '<div>Error: Missing form content.</div>'
    }
  }
}
